import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Activity, Loader2, LogOut, RefreshCw, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const INPUT =
  "h-12 w-full rounded-xl border border-[#272732] bg-[#0B0B10] px-4 text-sm text-white placeholder:text-zinc-600 transition-colors focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500";

const Field = ({ label, value }) =>
  value ? (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">{value}</p>
    </div>
  ) : null;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("vp_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState(null);
  const [contacts, setContacts] = useState([]);

  const authHeaders = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

  const load = async (t) => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/audit`, authHeaders(t)),
        axios.get(`${API}/contact`, authHeaders(t)),
      ]);
      setSubs(a.data.submissions);
      setContacts(c.data.contacts);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("vp_admin_token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("vp_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("vp_admin_token");
    setToken(null);
    setSubs(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#05050A] px-5">
        <div className="w-full max-w-sm rounded-3xl border border-[#272732] bg-[#121217] p-8">
          <img src="/assets/vp-mark.png" alt="Viral Pulse Agency" className="h-12 w-auto" />
          <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white">Admin Login</h1>
          <form onSubmit={login} className="mt-6 space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              className={INPUT}
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              className={INPUT}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-purple-600 py-3.5 text-sm font-bold text-white transition-colors hover:bg-purple-500 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Log In
            </button>
          </form>
          <Link to="/" data-testid="admin-back-to-site" className="mt-5 flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors hover:text-purple-400">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#05050A] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#05050A]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/assets/vp-mark.png" alt="Viral Pulse Agency" className="h-9 w-auto" />
            <span className="font-display text-base font-extrabold tracking-tight">Audit Submissions</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/10"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/10"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {subs === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <>
            <p data-testid="admin-submissions-count" className="text-sm font-bold text-zinc-400">
              {subs.length} audit submission{subs.length === 1 ? "" : "s"}
            </p>
            <div data-testid="admin-submissions-list" className="mt-6 space-y-5">
              {subs.length === 0 && (
                <p className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-zinc-500">
                  No submissions yet. They'll appear here as soon as someone requests a free audit.
                </p>
              )}
              {subs.map((s) => (
                <article
                  key={s.id}
                  data-testid={`submission-card-${s.id}`}
                  className="rounded-2xl border border-[#272732] bg-[#121217] p-6 sm:p-8"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-5">
                    <div>
                      <h2 className="font-display text-xl font-extrabold tracking-tight text-white">
                        {s.business_name}
                      </h2>
                      <p className="mt-1 text-sm text-zinc-400">
                        {s.name} • {s.email} • {s.phone}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="rounded-full bg-purple-600/15 px-3 py-1 text-xs font-bold text-purple-300">
                        Ad spend: {s.ad_spend}
                      </span>
                      <p className="mt-2 text-xs text-zinc-500">
                        {new Date(s.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label="Website" value={s.website} />
                    <Field label="Industry" value={s.industry} />
                    <Field label="City / Service Area" value={s.city} />
                    <Field label="Wants More Of" value={(s.wants || []).join(", ")} />
                    <Field label="Avg Customer Value" value={s.customer_value} />
                    <Field label="Getting Customers Via" value={(s.current_marketing || []).join(", ")} />
                    <Field label="Current Ad Spend" value={s.ad_spend} />
                  </div>
                </article>
              ))}
            </div>

            <h2 className="mt-14 font-display text-xl font-extrabold tracking-tight text-white">
              Contact Messages ({contacts.length})
            </h2>
            <div data-testid="admin-contacts-list" className="mt-5 space-y-4">
              {contacts.map((c) => (
                <div key={c.id} className="rounded-2xl border border-[#272732] bg-[#121217] p-5">
                  <p className="text-sm font-bold text-white">{c.name} <span className="font-medium text-zinc-400">• {c.email}</span></p>
                  <p className="mt-1.5 text-sm text-zinc-300">{c.message}</p>
                  <p className="mt-2 text-xs text-zinc-600">{new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
